// Force feedback model: turns what the car is doing into a steering torque.
//
// The core is the front tyres' self-aligning torque: lateral force times
// (pneumatic trail + mechanical trail). Pneumatic trail shrinks as slip angle
// grows, so the aligning torque peaks before the tyres reach peak grip and
// then falls away. The wheel going light is the cue that the front is about
// to wash out. A front lock-up collapses it too.
//
// On top of that: kerb rumble, off-track bumps, impacts, lock-up and gearshift
// kicks. The base torque updates with the physics (per frame); the vibration
// effects are synthesised at send time (~250 Hz) so a 30 Hz kerb rumble isn't
// aliased by a 60 Hz frame rate.
//
// Sign convention throughout: positive torque turns the wheel to the right.

import { wheelParams } from './settings';

export interface FfbInputs {
  /** Torque about the front steering axes from the tyres, N·m (+ = turns the wheel right). */
  aligningTorque: number;
  /** Speed, m/s. */
  speed: number;
  /** Surface under the car. */
  surface: 'track' | 'kerb' | 'runoff-asphalt' | 'grass' | 'gravel' | 'outside';
  /** Speed lost to a barrier impact this frame, m/s, and which side the barrier was (+1 right). */
  impact: number;
  impactSide: number;
  /** 0..1 how locked the front / spinning the rear wheels are. */
  frontLock: number;
  rearSpin: number;
  /** True for the frame a gear change happened. */
  shifted: boolean;
}

export interface FfbOutput {
  /** Hardware damper coefficient 0..1. */
  damper: number;
  /** Hardware friction coefficient 0..1. */
  friction: number;
}

interface Impulse {
  start: number;
  duration: number;
  amplitude: number;
}

/** Soft limiter: linear to `knee`, then eases into ±1 instead of clipping flat. */
export function softClip(x: number, knee = 0.8): number {
  const a = Math.abs(x);
  if (a <= knee) return x;
  const over = (a - knee) / (1 - knee);
  return Math.sign(x) * (knee + (1 - knee) * Math.tanh(over));
}

/**
 * Lift small forces above the wheel's gear/belt deadband: below ~4% the G27's
 * motor can't overcome its own friction, so tiny forces would feel like nothing.
 */
export function applyMinForce(x: number, min: number): number {
  const a = Math.abs(x);
  if (a < 0.002) return 0;
  return Math.sign(x) * Math.min(1, min + (1 - min) * a);
}

export class ForceFeedback {
  /** Filtered base torque, normalised (before gain). */
  private base = 0;
  private kerbAmp = 0;
  private kerbHz = 20;
  private roughAmp = 0;
  private lockAmp = 0;
  private spinAmp = 0;
  private impulses: Impulse[] = [];
  private noise = 0;
  private noiseTarget = 0;
  private noiseAt = 0;
  private phase = 0;
  private lastSample = 0;
  /** Debug: last components, for the setup screen's meter. */
  lastBase = 0;
  lastTotal = 0;
  enabled = true;

  constructor(private readonly random: () => number = Math.random) {}

  reset(): void {
    this.base = 0;
    this.impulses = [];
    this.kerbAmp = this.roughAmp = this.lockAmp = this.spinAmp = 0;
  }

  /** Feed the car state after the physics steps of a frame. `now` in seconds. */
  update(s: FfbInputs, dt: number, now: number): FfbOutput {
    const fx = wheelParams.effects.value;
    // Aligning torque, scaled so a high-downforce corner sits around 80%.
    const target = (s.aligningTorque / wheelParams.fullScaleNm.value) * wheelParams.aligning.value;
    const cutoff = wheelParams.smoothing.value;
    const a = 1 - Math.exp(-2 * Math.PI * cutoff * Math.max(dt, 0));
    this.base += a * (target - this.base);

    const speedFactor = Math.min(1, s.speed / 40);
    // Kerb ridges: rumble frequency follows speed (ridge spacing ~1.2 m), within what the G27 can render.
    this.kerbAmp = s.surface === 'kerb' && s.speed > 3 ? 0.16 * fx * (0.4 + 0.6 * speedFactor) : 0;
    this.kerbHz = Math.min(45, Math.max(8, s.speed / 1.2));
    this.roughAmp =
      (s.surface === 'grass' ? 0.07 : s.surface === 'gravel' ? 0.16 : s.surface === 'outside' ? 0.1 : s.surface === 'runoff-asphalt' ? 0.015 : 0.006) *
      fx *
      speedFactor;
    this.lockAmp = s.frontLock * 0.07 * fx * speedFactor;
    this.spinAmp = s.rearSpin * 0.03 * fx;

    if (s.impact > 1.5) {
      const amp = Math.min(1, s.impact / 12) * fx;
      // A hit on the right pushes the front wheels, and so the rim, to the left.
      this.impulses.push({ start: now, duration: 0.12, amplitude: -Math.sign(s.impactSide || 1) * amp });
    }
    if (s.shifted && s.speed > 1) this.impulses.push({ start: now, duration: 0.035, amplitude: (this.random() < 0.5 ? -1 : 1) * 0.1 * fx });
    this.impulses = this.impulses.filter((i) => now - i.start < i.duration);

    // Weight at standstill: tyres scrubbing on the spot resist the wheel.
    const parked = 1 - Math.min(1, s.speed / 8);
    // Damping grows with speed: the aligning torque's centring stiffness rises roughly
    // with downforce (~v²), and with ~25 ms of loop delay (frame, smoothing, USB, tyre
    // lag) a stiffer spring needs more damping to stay calm hands-off. 0.6x the setting
    // at low speed, 1.2x from ~290 km/h, plus extra when parked.
    const fast = Math.min(1, (s.speed / 80) ** 2);
    return {
      damper: Math.min(1, wheelParams.damper.value * (0.6 + 0.6 * fast) + 0.25 * parked * wheelParams.damper.value),
      friction: Math.min(1, wheelParams.friction.value + 0.35 * parked),
    };
  }

  /** Final torque −1..1 at time `now` (seconds), after gain, effects and the minimum force. */
  sample(now: number): number {
    if (!this.enabled) return 0;
    const dt = Math.min(0.05, Math.max(0, now - this.lastSample));
    this.lastSample = now;
    this.phase = (this.phase + this.kerbHz * dt) % 1;
    // Rough-surface noise: a new random target every ~25 ms, eased towards.
    if (now - this.noiseAt > 0.025) {
      this.noiseAt = now;
      this.noiseTarget = this.random() * 2 - 1;
    }
    this.noise += Math.min(1, dt / 0.012) * (this.noiseTarget - this.noise);

    let fx = 0;
    // A rounded square wave reads as ridges better than a sine on a geared wheel.
    fx += this.kerbAmp * Math.tanh(3 * Math.sin(2 * Math.PI * this.phase));
    fx += this.roughAmp * this.noise;
    fx += this.lockAmp * Math.sin(2 * Math.PI * 28 * now);
    fx += this.spinAmp * Math.sin(2 * Math.PI * 17 * now);
    for (const i of this.impulses) {
      const t = (now - i.start) / i.duration;
      if (t >= 0 && t < 1) fx += i.amplitude * Math.sin(Math.PI * t);
    }

    // The minimum force only lifts the steady torque; applying it to the
    // vibrations would turn faint road noise into a constant buzz.
    const gain = wheelParams.gain.value;
    const steady = applyMinForce(softClip(gain * this.base), wheelParams.minForce.value);
    this.lastBase = this.base;
    this.lastTotal = Math.max(-1, Math.min(1, steady + gain * fx));
    return this.lastTotal;
  }
}
